import React, { createContext, useCallback, useContext, useEffect, useState, useMemo, useRef } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'day8-ui';
import { fetchPlans } from '../actions/user';
import history from '../history';
import { cancelPlan, removeYachtFromPlan as removeYachtFromPlanAction, joinPlan as joinPlanAction, fetchActivePlans } from '../actions/planner';
import { buildBookingOptions, filterBookingOptions } from '../actions/bookingOptions';
import { buildYachtOptions, filterYachts } from '../actions/yachts';
import { validateUser } from '../actions/user';

export const initialValues = {
  plans: [],
  planExists: () => false,
  findExistingPlan: () => null,
  togglePlan: async () => {},
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  removeYachtFromPlan: async function(_alphaId) { },
  isUsingPlanner: false,
  setRedirectToPlanId: () => {},
  redirectToPlanId: null,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  signUp: async ( planCode, callback = () => {} ) => {}
};
export const PlannerContext = createContext(initialValues);

export const PlannerProvider = ({ children }) => {
  const dispatch = useDispatch();
  const loggedUser = useSelector(state => state.user.loggedUser);
  const destinations = useSelector(state => state.destinations.items);
  const cabins = useSelector(state => state.cabins.data);
  const boatWeeks = useSelector(state => state.yachts.items);
  const [plans, setPlans] = useState([]); // all plans where user is the BOOKER
  const [loggingIn, setLoggingIn] = useState(false);
  const [redirectToPlanId, setRedirectToPlanId] = useState(null);
  const [isUsingPlanner, setIsUsingPlanner] = useState(false); // the idea is to use this boolean to prevent users to create multiple plans like animals

  // used for preserving passed in plan action when user is not logged in yet
  const togglePlanArgs = useRef(null);
  // used for preserving plan code when user needs to join a plan after login
  const joinPlanArgs = useRef(null);
  useEffect(() => {
    if (!destinations?.length) {
      return;
    }
    // rebuild booking options/yachts with new plans of logged user
    dispatch(buildBookingOptions(destinations, cabins, plans));
    dispatch(filterBookingOptions());
    dispatch(buildYachtOptions(boatWeeks, plans));
    dispatch(filterYachts());
  }, [plans]);
  const addPlan = useCallback(async createPlanAction => {
    const res = await dispatch(createPlanAction());
    if (res.status === 'success') {
      setPlans(prevPlans => [...prevPlans, res.data.booking]);
    }
    return res;
  }, [dispatch]);

  const getPlansForProfile = useCallback(async () => {
    try {
      setIsUsingPlanner(true);
      const res = await fetchPlans();
      setPlans(res.data);
      return res.data;
    } catch (e) {
      console.error(e);
    } finally {
      setIsUsingPlanner(false);
    }
  }, []);

  // Sign up function for joining plans (moved from PlanJoin.js)
  const signUp = useCallback(async (planCode, callback = null) => {
    if (!loggedUser) {
      // Store the plan code and callback to execute after login
      joinPlanArgs.current = { planCode, callback };
      setLoggingIn(true);
      history.replace({
        state: {
          showLoginModal: true,
          returnTo: { pathname: `/plan/${planCode}` },
          page: 'signup',
          description: 'This trip is invite-only. To accept your invitation and join the crew, you\'ll need to log in or create a profile.'
        }
      });
      return { status: 'redirecting' };
    } else {
      try {
        const onSuccess = async () => {
          await dispatch(joinPlanAction(planCode));
          toast.success('Accepted invitation');
          // Execute callback if provided
          if (callback && typeof callback === 'function') {
            callback();
          }
        };
        await dispatch(validateUser(onSuccess));
        setLoggingIn(false);
        return { status: 'success' };
      } catch (error) {
        setLoggingIn(false);
        return { status: 'error', error };
      }
    }
  }, [loggedUser, dispatch, joinPlanArgs]);

  useEffect(() => {
    // if is coming back from login modal (so it is still logging in to true) handle plan actions based on remembered values from refs
    if (loggingIn && loggedUser) {
      if (togglePlanArgs.current && plans) {
        togglePlan(togglePlanArgs.current?.createPlanAction, togglePlanArgs.current?.identifier);
      } else if (joinPlanArgs.current) {
        // Automatically join the plan after successful login
        const { planCode, callback } = joinPlanArgs.current;
        const onSuccess = () => {
          dispatch(joinPlanAction(planCode));
          toast.success('Accepted invitation');
          // Execute callback if provided
          if (callback && typeof callback === 'function') {
            callback();
          }
        };
        dispatch(validateUser(onSuccess));
        joinPlanArgs.current = null; // Clear the stored plan code and callback
        setLoggingIn(false);
      }
    }
  }, [loggingIn, loggedUser, togglePlanArgs, plans, dispatch]);

  useEffect(() => {
    if (loggedUser) {
      getPlansForProfile(); // Fetch plans (where user is the BOOKER) on initial load if there is a logged-in user
      dispatch(fetchActivePlans()); // Fetch plans (booker and invited) to (count in dropdown)
    } else {
      setPlans([]); // Clear plans if no logged-in user
    }
  }, [loggedUser?.id, getPlansForProfile]);

  const removePlan = useCallback(async identifier => {
    const planAlphaId = plans.find(plan => plan.boatWeek?.id === identifier || plan.standardYachtWeek?.id === identifier)?.alphaId;
    if (!planAlphaId) return;
    const res = await new Promise((resolve, reject) => {
      dispatch(cancelPlan(planAlphaId))
        .then(response => {
          resolve(response);
        })
        .catch(error => {
          reject(error);
        });

    });
    if (res.status === 'success') {
      setPlans(prevPlans => prevPlans.filter(plan => plan.alphaId !== planAlphaId));
    }
  }, [plans, dispatch]);

  const removeYachtFromPlan = useCallback(async alphaId => {
    try {
      setIsUsingPlanner(true);
      const res = await dispatch(removeYachtFromPlanAction(alphaId));
      if (res.status === 'success') {
        // Update the plan to remove yacht selection but keep the plan
        setPlans(prevPlans => prevPlans.map(plan => {
          if (plan.alphaId === alphaId) {
            return {
              ...plan,
              boatWeek: null,
              standardYachtWeek: null
            };
          }
          return plan;
        }));
        // TODO: remove if proves not needed
        // dispatch(fetchYachtsAction(plan.destination.id));
        toast.success('Yacht removed');
      }
      return res;
    } catch (error) {
      console.error('Error removing yacht:', error);
      toast.error('Failed to remove yacht. Please try again.');
      throw error;
    } finally {
      setIsUsingPlanner(false);
    }
  }, [dispatch]);

  const planExists = useCallback(identifier => {
    return plans?.some(plan => plan.boatWeek?.id === identifier || plan.standardYachtWeek?.id === identifier);
  }, [plans]);

  const findExistingPlan = useCallback(identifier => {
    return plans?.find(plan => plan.boatWeek?.id === identifier || plan.standardYachtWeek?.id === identifier);
  }, [plans]);

  const togglePlan = useCallback(async (createPlanAction, identifier) => {
    if (!togglePlanArgs.current) {
      togglePlanArgs.current = { createPlanAction, identifier };
    }
    if (!loggedUser) {
      setLoggingIn(true);
      history.replace({
        search: location.search,
        state: {
          showLoginModal: true,
          returnTo: { pathname: location.pathname },
          page: 'signup',
          description: 'Saving a yacht to your Plans means it’s tied to your personal profile, so you can always come back to it later—no matter what device you’re on. Logging in ensures your Plans are securely stored, easy to access, and ready to share with your crew whenever you like.'
        }
      });
    } else {
      setLoggingIn(false);
      setIsUsingPlanner(true);
      try {
        if (planExists(identifier)) {
          // remove plan
          await removePlan(identifier);
          toast.success('Plan removed');
        } else {
          // add plan
          const res = await addPlan(createPlanAction);
          toast.success('We\'ve saved this yacht to your profile. Use the planner app to share it with friends and organise your holiday together.', {
            action: {
              label: 'Open Planner',
              onClick: () => window.open(`/plan/${res.data?.booking.alphaId}`, '_blank', 'noopener,noreferrer')
            }
          });
        }
        togglePlanArgs.current = null;
        // return res;
      } catch (e) {
        console.error(e);
        toast.error('Something went wrong. Please try again.');
      } finally {
        setIsUsingPlanner(false);
      }
    }
  });
  const contextValue = useMemo(() => ({
    plans,
    planExists,
    findExistingPlan,
    togglePlan,
    removeYachtFromPlan,
    isUsingPlanner,
    setRedirectToPlanId,
    redirectToPlanId,
    signUp
  }), [plans, togglePlan, planExists, findExistingPlan, removeYachtFromPlan, isUsingPlanner, setRedirectToPlanId, redirectToPlanId, signUp]);
  return (
    <PlannerContext.Provider value={contextValue}>
      {children}
    </PlannerContext.Provider>
  );
};

PlannerProvider.propTypes = {
  children: PropTypes.any
};

export const usePlanner = () => useContext(PlannerContext);

export const withPlanner = WrappedComponent => {
  return function PlannerComponent(props) {
    return (
      <PlannerContext.Consumer>
        {context => <WrappedComponent {...props} plannerContext={context} />}
      </PlannerContext.Consumer>
    );
  };
};